import _slicedToArray from 'babel-runtime/helpers/slicedToArray';
import React, { useReducer, useEffect, useMemo } from 'react';
import PropTypes from 'prop-types';
import queryString from 'query-string';
import { navigate } from '@reach/router';

import CollectionCard from '../../components/CollectionCard/CollectionCard';
import CollectionFilters from '../../components/CollectionFilters/CollectionFilters';
import CollectionPagination from '../../components/CollectionPagination/CollectionPagination';
import ActiveFilters from '../../components/ActiveFilters/ActiveFilters';
import CollectionSort from '../../components/CollectionSort/CollectionSort';
import CollectionSubMenu from '../../components/CollectionSubMenu/CollectionSubMenu';
import NoProductsCard from '../../components/NoProductsCard/NoProductsCard';
import Loading from '../../components/Loading/Loading';
import ProductModal from '../../components/ProductModal/ProductModal';
import CloseBtn from '../../components/CloseBtn/CloseBtn';
import MobileFilterToggle from '../../components/MobileFilterToggle/MobileFilterToggle';

import { getActiveFilters, getFilteredProducts, getProductsToDisplay, makeFilterQueryAnArray, buildQueryString, getSubMenu } from '../../Utils/CollectionUtils';

import { StyledCollection, StyledCollectionWrapper, StyledCollectionGrid, StyledCollectionTop } from './CollectionStyles';

var collectionReducer = function collectionReducer(state, action) {
  switch (action.type) {
    case 'ALL_PRODUCTS_SET':
      return Object.assign({}, state, {
        runFilterOnFetchComplete: false
      });

    case 'QUERY_PARAMS_CHANGED':
      return Object.assign({}, state, {
        activeFilters: action.newActiveFilters,
        pageIndex: action.newPageIndex,
        showMobileFilterCheck: action.showMobileFilterCheck,
        activeFilterValues: action.newActiveFilterValues
      });

    case 'SET_FILTERED_PRODUCTS':
      return Object.assign({}, state, {
        activeFilterCount: action.newActiveFilterCount,
        filteredProducts: action.newFilteredProducts,
        runFilterOnFetchComplete: action.runFilterOnFetchComplete
      });

    case 'TOGGLE_MOBILE_FILTER':
      return Object.assign({}, state, {
        showMobileFilter: action.toggle,
        showMobileFilterCheck: false
      });

    case 'SORT_PRODUCTS':
      return Object.assign({}, state, {
        sortBy: action.sortBy
      });

    case 'TOGGLE_MODAL':
      return Object.assign({}, state, {
        modalActive: !state.modalActive,
        modalProduct: action.modalProduct
      });

    case 'TOGGLE_VIEW_ALL':
      return Object.assign({}, state, {
        productsPerPage: action.productsPerPage
      });

    default:
      return state;
  }
};

var _ref = React.createElement(NoProductsCard, null);

var Collection = function Collection(props) {
  var _useReducer = useReducer(collectionReducer, {
    activeFilters: {},
    activeFilterValues: [],
    activeFilterCount: 0,
    filteredProducts: [],
    displayedProducts: [],
    pageIndex: 0,
    runFilterOnFetchComplete: false,
    sortBy: 'best-selling',
    showMobileFilter: false,
    showMobileFilterCheck: false,
    modalActive: false,
    modalProduct: null,
    productsPerPage: 24
  }),
      _useReducer2 = _slicedToArray(_useReducer, 2),
      state = _useReducer2[0],
      dispatch = _useReducer2[1];

  var allProducts = useMemo(function () {
    if (props.storeFrontProductCount === props.collectionProductCount) {
      dispatch({ type: 'ALL_PRODUCTS_SET' });
      return props.products;
    } else {
      return [];
    }
    // don't want to include props.products because
    // props.storeFrontProductCount is props.products.length
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.storeFrontProductCount, props.collectionProductCount]);

  useEffect(function () {
    var updateStateFromQueryParams = function updateStateFromQueryParams() {
      var parsedQueryParam = makeFilterQueryAnArray(queryString.parse(props.queryParams));
      var filters = parsedQueryParam.filter;
      var newActiveFilters = filters ? getActiveFilters(filters) : {};
      var newPageIndex = parseInt(parsedQueryParam.page) - 1 || 0;
      dispatch({
        type: 'QUERY_PARAMS_CHANGED',
        showMobileFilterCheck: filters ? true : false,
        runFilterOnFetchComplete: props.hasMoreProductData,
        newActiveFilterValues: filters ? filters : [],
        newActiveFilters: newActiveFilters,
        newPageIndex: newPageIndex
      });
    };
    updateStateFromQueryParams();
  }, [props.queryParams, props.hasMoreProductData, state.showMobileFilter]);

  // For DY Billboard Interrupt
  // useEffect(() => {
  //   if (state.pageIndex > 0) {
  //     DY.API('event', {
  //       name: 'HIDE_DY_BILLBOARD',
  //     });
  //   }
  // }, [state.pageIndex]);

  useEffect(function () {
    if (props.isCartOpen) {
      toggleProductModal();
    }
  }, [props.isCartOpen]);

  useEffect(function () {
    var newFilteredProducts = getFilteredProducts(state.activeFilters, allProducts);
    dispatch({
      type: 'SET_FILTERED_PRODUCTS',
      newFilteredProducts: newFilteredProducts,
      runFilterOnFetchComplete: props.hasMoreProductData && Object.keys(state.activeFilters).length > 0,
      newActiveFilterCount: state.activeFilterValues.length
    });
    // adding props.hasMoreProductData causes this effect to run on load every time
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state.runFilterOnFetchComplete, state.activeFilters]);

  var scrollToTop = function scrollToTop() {
    window.scrollTo(0, 0);
  };

  var handleFilterClick = function handleFilterClick(tag) {
    var filter = { filter: tag };
    var newParams = Object.assign({}, filter);
    buildUrlQuery(newParams);
    scrollToTop();
  };

  var handleFilterClearAll = function handleFilterClearAll() {
    dispatch({ type: 'TOGGLE_MOBILE_FILTER', toggle: false });
    clearUrlQueryParams();
  };

  var toggleViewAll = function toggleViewAll() {
    dispatch({
      type: 'TOGGLE_VIEW_ALL',
      productsPerPage: state.productsPerPage === 24 ? props.collectionProductCount : 24
    });
    // go to page 1 since viewing all
    var newParams = { page: '1' };
    buildUrlQuery(newParams);
    // if fetching more products or going from view all back to pagination, scroll up!
    if (props.hasMoreProductData || state.productsPerPage !== 24 || state.pageIndex !== 0) {
      scrollToTop();
    }
  };

  var handlePaginationClick = function handlePaginationClick(index, scrollUp) {
    var newParams = { page: (index + 1).toString() };
    buildUrlQuery(newParams);
    scrollUp ? scrollToTop() : null;
  };

  var handleSortByChange = function handleSortByChange(e) {
    var pageQuery = { sort: e.target.value, page: '1' };
    dispatch({ type: 'SORT_PRODUCTS', sortBy: e.target.value });
    buildUrlQuery(pageQuery);
  };

  var toggleMobileFilterPopup = function toggleMobileFilterPopup() {
    dispatch({ type: 'TOGGLE_MOBILE_FILTER', toggle: !state.showMobileFilter });
  };

  var toggleProductModal = function toggleProductModal(product) {
    dispatch({ type: 'TOGGLE_MODAL', modalProduct: product });
  };

  var buildUrlQuery = function buildUrlQuery(newParam) {
    var query = queryString.parse(props.queryParams);
    var newQueryString = buildQueryString(query, newParam);

    // Removes question mark if params are blank
    newQueryString === '' ? clearUrlQueryParams() : navigate('?' + newQueryString);
  };

  var clearUrlQueryParams = function clearUrlQueryParams() {
    navigate(props.location.pathname);
  };

  var giftCardCollection = props.handle === 'gift-cards'; // hide filters on gift card pages

  var subMenu = getSubMenu(props.mainMenu);

  var productsToDisplay = getProductsToDisplay(state.activeFilterCount, props.products, state.filteredProducts, state.sortBy, state.productsPerPage, state.pageIndex);
  return React.createElement(
    StyledCollectionWrapper,
    null,
    React.createElement(
      StyledCollection,
      {
        className: 'Collection',
        giftCardCollection: giftCardCollection
      },
      !giftCardCollection && React.createElement(CollectionFilters, {
        activeFilters: state.activeFilters,
        activeFilterCount: state.activeFilterCount,
        filteredProducts: state.filteredProducts,
        collectionFilters: props.filterData.collectionFilters,
        artistFilters: props.filterData.dormifyXYouFilters,
        allCollectionTags: props.filterData.allCollectionTags,
        showMobileFilter: state.showMobileFilter,
        toggleMobileFilterPopup: toggleMobileFilterPopup,
        handleFilterClick: handleFilterClick,
        handleFilterClearAll: handleFilterClearAll,
        subMenu: subMenu,
        dormifyXYouPage: props.dormifyXYouPage
      }),
      React.createElement(
        'div',
        null,
        subMenu && React.createElement(CollectionSubMenu, {
          subMenu: subMenu,
          currentUrl: window.location.pathname,
          isTouchDevice: props.isTouchDevice
        }),
        React.createElement(
          StyledCollectionTop,
          null,
          React.createElement(
            'h1',
            null,
            props.title
          ),
          !giftCardCollection && React.createElement(MobileFilterToggle, {
            onClick: toggleMobileFilterPopup,
            activeFilterCount: state.activeFilterCount
          }),
          React.createElement(CollectionSort, {
            handleSortByChange: handleSortByChange,
            sortBy: state.sortBy
          }),
          React.createElement(ActiveFilters, {
            activeFilters: state.activeFilterValues,
            handleFilterClick: handleFilterClick,
            handleFilterClearAll: handleFilterClearAll
          }),
          React.createElement(CollectionPagination, {
            productsPerPage: state.productsPerPage,
            currentPageIndex: state.pageIndex,
            currentlyFiltered: Object.keys(state.activeFilters).length > 0,
            filteredProductCount: state.filteredProducts.length,
            totalProductCount: props.collectionProductCount,
            bottomPagination: false,
            handlePaginationClick: handlePaginationClick
          })
        ),
        React.createElement(
          StyledCollectionGrid,
          { className: 'collection-grid-for-dy' },
          state.runFilterOnFetchComplete || props.hasMoreProductData && (state.pageIndex > 0 || state.productsPerPage > 24) ? React.createElement(Loading, {
            text: props.hasMoreProductData ? 'Fetching more items....' : 'Loading...'
          }) : productsToDisplay.length ? productsToDisplay.map(function (product) {
            return React.createElement(CollectionCard, {
              addVariantToCart: props.addVariantToCart,
              checkout: props.checkout,
              key: product.node.id,
              product: product.node,
              openProductModal: toggleProductModal,
              isTouchDevice: props.isTouchDevice,
              isWishlistProduct: props.wishlistProducts.includes(product.node.title),
              toggleAccountModal: props.toggleAccountModal,
              lowStockThreshold: props.lowStockThreshold
            });
          }) : _ref
        ),
        React.createElement(CollectionPagination, {
          productsPerPage: state.productsPerPage,
          handlePaginationClick: handlePaginationClick,
          currentPageIndex: state.pageIndex,
          currentlyFiltered: Object.keys(state.activeFilters).length > 0,
          filteredProductCount: state.filteredProducts.length,
          bottomPagination: true,
          totalProductCount: props.collectionProductCount,
          toggleViewAll: toggleViewAll
        })
      )
    ),
    React.createElement(ProductModal, {
      active: state.modalActive,
      product: state.modalProduct,
      addVariantToCart: props.addVariantToCart,
      checkout: props.checkout,
      closeProductModal: toggleProductModal
    }),
    React.createElement(CloseBtn, {
      visible: state.showMobileFilter,
      onClick: toggleMobileFilterPopup,
      showMobileFilterCheck: state.showMobileFilterCheck,
      btnType: 'Mobile Filter'
    })
  );
};

Collection.propTypes = {
  addVariantToCart: PropTypes.func.isRequired,
  hasMoreProductData: PropTypes.bool.isRequired,
  checkout: PropTypes.object.isRequired,
  title: PropTypes.string.isRequired,
  handle: PropTypes.string.isRequired,
  products: PropTypes.array.isRequired,
  isTouchDevice: PropTypes.bool,
  wishlistProducts: PropTypes.array,
  location: PropTypes.object.isRequired,
  queryParams: PropTypes.string.isRequired,
  isCartOpen: PropTypes.bool.isRequired,
  storeFrontProductCount: PropTypes.number,
  collectionProductCount: PropTypes.number,
  mainMenu: PropTypes.array,
  filterData: PropTypes.object,
  toggleAccountModal: PropTypes.func.isRequired,
  lowStockThreshold: PropTypes.string,
  dormifyXYouPage: PropTypes.bool
};

export default Collection;